/**
 * PublisherContactPage - Contact page specifically for publishers interested in 
 * bringing their catalog to NewAudia for conversion and playback
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Building2, Send, BookOpen, ArrowLeft, CheckCircle, Users, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublisherContactPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    catalogSize: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.companyName || !formData.contactName || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send email via backend
      const response = await fetch(`${API_URL}/api/contact/publisher-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: formData.companyName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          catalog_size: formData.catalogSize,
          message: formData.message,
          to_email: 'info@NewAudia.com',
          subject: `Publisher Inquiry: ${formData.companyName}`
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Your inquiry has been sent successfully!');
        
        // Return to landing page after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2500);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending publisher inquiry:', error);
      toast.error('Failed to send your inquiry. Please try again or call us at (719) 205-6381.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Thank You!</h2>
          <p className="text-slate-300 mb-6">
            Your inquiry has been submitted successfully. Our team will review your information and reach out to discuss how NewAudia can work with your catalog.
          </p>
          <p className="text-sm text-slate-400">
            Redirecting you to the homepage...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
          <img src="/newaudia-logo.png" alt="NewAudia" className="h-10" />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-500/20 rounded-full mb-6">
            <Building2 className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Welcome, Publisher!
          </h1>
          <p className="text-lg text-slate-300 mb-2">
            We're excited about the opportunity to bring your catalog to NewAudia.
          </p>
          <p className="text-slate-400">
            Fill out the form below and our team will help you get started.
          </p>
        </div>

        {/* Benefits Banner */}
        <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border border-purple-500/30 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-purple-400" />
            Publisher Benefits
          </h3>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-300">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              Dedicated publisher account
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              First manuscript free per author
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              Catalog-wide analytics
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              Author commission tracking
            </li>
            <li className="flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400 flex-shrink-0" />
              Author management tools
            </li>
            <li className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-400 flex-shrink-0" />
              Revenue reporting
            </li>
          </ul>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-white mb-6">Tell Us About Your Publishing House</h2>
          
          <div className="space-y-5">
            {/* Company Name */}
            <div>
              <Label htmlFor="companyName" className="text-slate-300 mb-2 block">
                Publishing Company Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Your publishing company name"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-purple-500"
                required
              />
            </div>

            {/* Contact Name */}
            <div>
              <Label htmlFor="contactName" className="text-slate-300 mb-2 block">
                Contact Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="contactName"
                name="contactName"
                type="text"
                value={formData.contactName}
                onChange={handleChange}
                placeholder="Your full name"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-purple-500"
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-slate-300 mb-2 block">
                Email Address <span className="text-red-400">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="contact@publisher.com"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-purple-500"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-slate-300 mb-2 block">
                Phone Number <span className="text-slate-500">(optional)</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-purple-500"
              />
            </div>

            {/* Website */}
            <div>
              <Label htmlFor="website" className="text-slate-300 mb-2 block">
                Company Website <span className="text-slate-500">(optional)</span>
              </Label>
              <Input
                id="website"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.yourcompany.com"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-purple-500"
              />
            </div>

            {/* Catalog Size */}
            <div>
              <Label htmlFor="catalogSize" className="text-slate-300 mb-2 block">
                Approximate Catalog Size <span className="text-slate-500">(optional)</span>
              </Label>
              <Input
                id="catalogSize"
                name="catalogSize"
                type="text"
                value={formData.catalogSize}
                onChange={handleChange}
                placeholder="e.g., 50 titles, 200+ titles, etc."
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-purple-500"
              />
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-slate-300 mb-2 block">
                Message <span className="text-red-400">*</span>
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your publishing house, the types of titles you publish, and how you'd like to work with NewAudia..."
                rows={5}
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-purple-500 resize-none"
                required
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              data-testid="publisher-contact-submit-btn"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Send className="w-5 h-5" />
                  Submit Inquiry
                </span>
              )}
            </Button>
          </div>

          {/* Contact Alternative */}
          <p className="text-center text-sm text-slate-400 mt-6">
            Prefer to talk? Call us directly at{' '}
            <a href="tel:+17192056381" className="text-purple-400 hover:text-purple-300 font-semibold">
              (719) 205-6381
            </a>
          </p>
        </form>

        {/* Accepted Formats Note */}
        <div className="mt-8 text-center text-sm text-slate-400">
          <p>
            <span className="text-slate-300 font-medium">Accepted formats:</span> EPUB and Microsoft Word (.docx or .doc)
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublisherContactPage;
